import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthCard } from "@/components/ax/AuthCard";
import { Button } from "@/components/ax/Button";
import { Switch } from "@/components/ax/Switch";
import { useI18n } from "@/i18n";
import { getConsent, setConsent } from "@/lib/mockAuth";

const ROWS = [
  { key: "data", required: true },
  { key: "ai" },
  { key: "analytics" },
];

export default function Onboarding() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [value, setValue] = useState(() => ({ data: false, ai: false, analytics: false, ...getConsent() }));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const toggle = (key) => (next) => {
    setFailed(false);
    setValue((v) => ({ ...v, [key]: next }));
  };

  const submit = async () => {
    if (busy || !value.data) return;
    setBusy(true);
    setFailed(false);
    const forceFail = new URLSearchParams(window.location.search).get("fail") === "1";
    const res = await setConsent(value, { fail: forceFail });
    setBusy(false);
    if (res.error) setFailed(true);
    else navigate("/");
  };

  return (
    <AuthCard title={t("consent.title")} testId="onboarding-page">
      <ul className="m-0 mt-4 flex list-none flex-col gap-3 p-0">
        {ROWS.map(({ key, required }) => (
          <li
            key={key}
            data-testid={`consent-row-${key}`}
            className="flex min-h-[72px] items-start gap-3 rounded-[12px] border border-ax-border bg-ax-bg p-4"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span id={`consent-${key}-title`} className="text-base font-medium leading-6 text-ax-text">
                  {t(`consent.${key}.title`)}
                </span>
                {required && (
                  <span className="inline-flex h-6 items-center rounded-[8px] border border-ax-border px-2 font-mono text-[13px] uppercase text-ax-text-2">
                    {t("consent.data.required")}
                  </span>
                )}
              </div>
              <p id={`consent-${key}-desc`} className="m-0 mt-1 text-sm leading-5 text-ax-text-2">
                {t(`consent.${key}.desc`)}
              </p>
              <p
                data-testid={`consent-${key}-state`}
                className="m-0 mt-2 font-mono text-[13px] uppercase leading-[18px] text-ax-muted"
              >
                {value[key] ? t("consent.on") : t("consent.off")}
              </p>
            </div>
            <Switch
              checked={value[key]}
              onChange={toggle(key)}
              labelledBy={`consent-${key}-title`}
              describedBy={`consent-${key}-desc`}
              testId={`consent-${key}-switch`}
            />
          </li>
        ))}
      </ul>
      {failed && (
        <p role="alert" data-testid="consent-error" className="m-0 mt-4 text-sm leading-5 text-ax-error">
          {t("consent.fail")}
        </p>
      )}
      <Button
        variant="primary"
        busy={busy}
        disabled={!value.data || busy}
        onClick={submit}
        data-testid="consent-continue-button"
        className="mt-6 w-full"
      >
        {busy ? t("consent.busy") : t("consent.continue")}
      </Button>
      <Link
        to="/kebijakan"
        data-testid="policy-link"
        className="mt-2 inline-flex min-h-[48px] items-center text-sm font-medium text-ax-link hover:underline"
      >
        {t("consent.policy")}
      </Link>
    </AuthCard>
  );
}
